import { b64toBlob } from './downloadBase64';

/** MIME types por extensión (VDR) */
export const EXTENSION_TO_MIME: Record<string, string> = {
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  svg: 'image/svg+xml',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  mp4: 'video/mp4',
  mov: 'video/quicktime',
  avi: 'video/x-msvideo',
  ics: 'text/calendar',
  zip: 'application/zip',
};

const BASE64_PREFIX = 'base64,';

/**
 * Extrae el MIME type del data URL (ej: data:application/pdf;base64,xxx).
 * Retorna null si no hay prefijo base64 válido.
 */
export function getMimeFromBase64DataUrl( dataUrl: string ): string | null {
  if ( !dataUrl || typeof dataUrl !== 'string' ) return null;
  const idx = dataUrl.indexOf( BASE64_PREFIX );
  if ( idx === -1 ) return null;
  const mimePart = dataUrl.substring( 5, idx - 1 );
  return mimePart && mimePart.includes( '/' ) ? mimePart : null;
}

/** MIME de PDF */
export const MIME_PDF = 'application/pdf';

/**
 * Indica si el documento es PDF (por base64 o extensión .pdf).
 */
export function isPdfDocument( dataUrl: string | null, filename: string ): boolean {
  const fromData = dataUrl ? getMimeFromBase64DataUrl( dataUrl ) : null;
  if ( fromData ) return fromData === MIME_PDF;
  const ext = filename?.split( '.' ).pop()?.toLowerCase();
  return ext === 'pdf';
}

/**
 * Obtiene MIME desde extensión. Solo PDF se infiere desde el nombre;
 * el resto debe venir en el base64.
 */
export function getMimeFromFilename( filename: string ): string | null {
  if ( !filename ) return null;
  const ext = filename.split( '.' ).pop()?.toLowerCase();
  return ext ? EXTENSION_TO_MIME[ext] ?? null : null;
}

/**
 * Extrae el payload base64 puro (sin data:xxx;base64,) del data URL.
 */
export function extractBase64Payload( dataUrl: string ): string | null {
  if ( !dataUrl || typeof dataUrl !== 'string' ) return null;
  const idx = dataUrl.indexOf( BASE64_PREFIX );
  if ( idx === -1 ) return null;
  return dataUrl.substring( idx + BASE64_PREFIX.length );
}

/**
 * Resuelve el MIME: primero del data URL, si no del filename (solo PDF).
 */
function resolveMime( dataUrl: string, filename: string ): string {
  const fromData = getMimeFromBase64DataUrl( dataUrl );
  if ( fromData ) return fromData;
  const fromFile = getMimeFromFilename( filename );
  if ( fromFile ) return fromFile;
  return 'application/octet-stream';
}

const MIN_PAYLOAD_LENGTH = 100;

/**
 * Valida que el payload base64 sea usable (no vacío/corrupto).
 */
export function isValidBase64Payload( dataUrl: string ): boolean {
  const payload = extractBase64Payload( dataUrl );
  return !!( payload && payload.length >= MIN_PAYLOAD_LENGTH );
}

/**
 * Crea un blob URL desde data URL base64 (para visor PDF).
 * @throws Error si el documento está dañado o vacío
 */
export function createBlobUrlFromBase64DataUrl(
  dataUrl: string,
  filename: string,
): string {
  const payload = extractBase64Payload( dataUrl );
  if ( !payload || payload.length < MIN_PAYLOAD_LENGTH ) {
    throw new Error( 'El documento está dañado o vacío. No se puede visualizar.' );
  }
  const mime = resolveMime( dataUrl, filename );
  const blob = b64toBlob( payload, mime );
  return URL.createObjectURL( blob );
}

/**
 * Descarga un archivo desde data URL base64.
 * @param dataUrl - string con formato data:application/pdf;base64,xxx (u otro MIME)
 * @param filename - nombre del archivo (obligatorio, debe venir de la entidad)
 * @throws Error si no hay filename
 */
export function downloadFileFromBase64( dataUrl: string, filename: string ): void {
  if ( !filename || typeof filename !== 'string' ) {
    throw new Error( 'El nombre del archivo es obligatorio para la descarga.' );
  }

  const payload = extractBase64Payload( dataUrl );
  if ( !payload || payload.length < MIN_PAYLOAD_LENGTH ) {
    throw new Error( 'El documento está dañado o vacío. No se puede descargar.' );
  }

  const mime = resolveMime( dataUrl, filename );
  const blob = b64toBlob( payload, mime );
  const blobUrl = URL.createObjectURL( blob );

  const link = document.createElement( 'a' );
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild( link );
  link.click();
  document.body.removeChild( link );
  URL.revokeObjectURL( blobUrl );
}
